import 'keen-slider/keen-slider.min.css'
import KeenSlider from 'keen-slider'

// TODO: Implement product tile hover effect

function formatMoney(amount, currencyCode) {
	const format = new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode })
	return format.format(amount)
}

export default function RecommendedProducts() {
	return {
		// State
		isLoading: true,
		products: [],
		gids: [],
		sliderInstance: null,

		// Initialization
		init() {
			this.parseGids()
			if (this.gids.length > 0) {
				this.fetchProducts()
			} else {
				// No GIDs found, stop loading and render empty state
				this.isLoading = false
				this.render()
			}
		},

		// Parse GIDs from data attribute
		parseGids() {
			const gidsString = this.$el.dataset.recommendedProductGids
			if (!gidsString) {
				console.warn('Recommended products: No GIDs found in data attribute.')
				this.gids = []
				return
			}
			try {
				// Decode HTML entities (like &quot;) before parsing JSON
				const decodedGidsString = gidsString.replace(/&quot;/g, '"')
				const parsedGids = JSON.parse(decodedGidsString)
				if (!Array.isArray(parsedGids)) {
					throw new Error('Parsed GIDs is not an array.')
				}
				this.gids = parsedGids
			} catch (e) {
				console.error(
					'Recommended products: Failed to parse GIDs.',
					e,
					'Raw string:',
					gidsString,
				)
				this.gids = []
			}
		},

		// Fetch product data from Storefront API
		async fetchProducts() {
			this.isLoading = true
			this.render() // Optionally show loading state immediately

			const query = `
        query getProductsByIds($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on Product {
              id
              title
              onlineStoreUrl
              featuredImage {
                url(transform: {maxWidth: 300, maxHeight: 300, crop: CENTER})
                altText
                width
                height
              }
              variants(first: 1) {
                edges {
                  node {
                    price {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        }
      `

			try {
				const response = await fetch('/api/2024-04/graphql.json', {
					// Ensure API version is current
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						// IMPORTANT: Replace with your actual public Storefront API token
						'X-Shopify-Storefront-Access-Token': '9af1655b8dffea23f3969cf7b46fae0a',
					},
					body: JSON.stringify({
						query: query,
						variables: { ids: this.gids },
					}),
				})

				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`)
				}

				const jsonResponse = await response.json()

				if (jsonResponse.errors) {
					console.error('Recommended products: GraphQL Errors:', jsonResponse.errors)
					throw new Error('Error fetching product data from GraphQL.')
				}

				// Filter out any null results (e.g., from invalid GIDs)
				this.products = (jsonResponse.data?.nodes || []).filter((p) => p !== null)
			} catch (error) {
				console.error('Recommended products: Error fetching data:', error)
				this.products = [] // Reset products on error
			} finally {
				this.isLoading = false
				this.render() // Render final state (products or empty message)
			}
		},

		// Render product cards into the grid
		render() {
			const grid = this.$el.querySelector('.recommended-products-grid')
			if (!grid) {
				console.error('Recommended products: Could not find grid element.')
				return
			}

			// Destroy existing slider instance before clearing content
			if (this.sliderInstance) {
				this.sliderInstance.destroy()
				this.sliderInstance = null
			}

			// Clear previous content (placeholder or old cards)
			grid.innerHTML = ''

			if (this.isLoading) {
				grid.innerHTML =
					'<div class="loading-placeholder col-span-full text-center text-gray-500">Loading recommendations...</div>'
				return
			}

			if (!this.products || this.products.length === 0) {
				grid.innerHTML = ''
				return
			}

			let productCardsHtml = ''
			this.products.forEach((product) => {
				const firstVariant = product.variants.edges[0]?.node
				const price = firstVariant
					? formatMoney(firstVariant.price.amount, firstVariant.price.currencyCode)
					: ''
				const imageUrl = product.featuredImage?.url
				const imageAlt = product.featuredImage?.altText || product.title

				// Add the keen-slider__slide class to each product card wrapper
				productCardsHtml += `
          <div class="keen-slider__slide">
            <a href="${product.onlineStoreUrl}" class="block group">
				<div class="aspect-[160/228]">
              ${
					imageUrl
						? `
                <img
                  src="${imageUrl}"
                  alt="${imageAlt}"
                  width="300"
                  height="300"
                  loading="lazy"
                  class="w-full h-full object-cover"
                >`
						: ``
				}
              </div>
              <div class="16">
                <h3>${product.title}</h3>
                ${price ? `<p>${price}</p>` : ''}
              </div>
            </a>
          </div>
        `
			})

			grid.innerHTML = productCardsHtml

			// Initialize KeenSlider after rendering products
			this.sliderInstance = new KeenSlider(grid, {
				breakpoints: {
					'(min-width: 800px)': {
						slides: { perView: 3.5, spacing: 0 },
					},
					'(min-width: 1000px)': {
						slides: { perView: 4.5, spacing: 0 },
					},
				},
				slides: { perView: 2.5, spacing: 0 },
			})
		},
	}
}
